import { MapPin, Phone } from "lucide-react";
import { SERVICE_AREAS } from "@/lib/mock";

const CITY_LINKS = {
  "O'Fallon": "/flooring-installation-ofallon-mo",
  "Wentzville": "/flooring-installation-wentzville-mo",
  "St. Peters": "/flooring-installation-st-peters-mo",
  "St. Charles": "/flooring-installation-st-charles-mo",
  "Chesterfield": "/flooring-installation-chesterfield-mo",
  "Kirkwood": "/flooring-installation-kirkwood-mo",
  "Ballwin": "/flooring-installation-ballwin-mo",
  "Clayton": "/flooring-installation-clayton-mo",
};

const NEIGHBORHOODS = [
  // St. Charles County
  "Winghaven",
  "Villages of Dardenne",
  "Whitmoor",
  "Twin Chimneys",
  "Bear Creek",
  "Stone Meadows",
  "Persimmon Woods",
  "Cottleville",
  "Dardenne Prairie",
  "Lake Saint Louis",
  // St. Louis County
  "Wildhorse",
  "Kehrs Mill Trails",
  "Bellerive",
  "Baxter Ridge",
  "Highcroft Ridge",
  // City of St. Louis
  "Central West End",
  "The Hill",
  "Soulard",
  "Lafayette Square",
  "Tower Grove",
  "Shaw",
  "Benton Park",
];

export default function ServiceAreas() {
  return (
    <section
      id="areas"
      data-testid="service-areas-section"
      className="py-16 lg:py-24 bg-stone-50"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        {/* Header */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-12 lg:mb-16 items-end">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 text-xs font-semibold text-orange-600 mb-4">
              <span className="w-1.5 h-1.5 bg-orange-500 rounded-full" />
              WHERE WE WORK
            </div>
            <h2 className="font-display font-semibold text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-[-0.025em] text-stone-900">
              Serving the
              <br />
              <span className="text-orange-600">St. Louis metro.</span>
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="text-stone-600 leading-relaxed text-lg">
              Family-owned and based locally. We&apos;ve installed projects across
              every neighborhood from downtown St. Louis to St. Charles County.
            </p>
            <a
              href="tel:+16368496681"
              data-testid="areas-cta"
              className="mt-5 inline-flex items-center gap-2 text-stone-900 font-semibold hover:text-orange-600 transition-colors"
            >
              <Phone size={16} strokeWidth={2.25} />
              Not in our area? Call us — 636-849-6681
            </a>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          {/* MAP */}
          <div className="lg:col-span-7 order-2 lg:order-1">
            <div className="relative overflow-hidden rounded-3xl border border-stone-200 aspect-[5/4] md:aspect-[16/11] bg-stone-200 shadow-[0_24px_60px_-24px_rgba(28,25,23,0.25)]">
              <iframe
                title="M&L Exterior and Interior Design — Service area in Greater St. Louis"
                src="https://maps.google.com/maps?q=St.+Louis,+MO&z=9&output=embed"
                width="100%"
                height="100%"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="absolute inset-0 h-full w-full border-0"
                data-testid="service-area-map"
              />
              {/* Top corner badge */}
              <div className="absolute top-4 left-4 inline-flex items-center gap-2 bg-stone-50/95 backdrop-blur-md px-3.5 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-wider text-stone-900 pointer-events-none">
                <span className="w-1.5 h-1.5 bg-orange-500 rounded-full animate-pulse-dot" />
                Coverage area
              </div>
            </div>
          </div>

          {/* AREA LIST */}
          <div className="lg:col-span-5 order-1 lg:order-2">
            <div className="grid grid-cols-2 gap-3">
              {SERVICE_AREAS.map((a) => {
                const href = CITY_LINKS[a];
                const testId = `area-${a.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "")}`;
                const commonClass =
                  "group flex items-center gap-3 bg-white border border-stone-200 rounded-full px-4 py-3 transition-all";
                const contents = (
                  <>
                    <MapPin
                      size={14}
                      strokeWidth={2.25}
                      className="text-orange-600 shrink-0"
                    />
                    <span className="font-medium text-stone-800 text-sm">
                      {a}
                    </span>
                    {href && (
                      <span className="ml-auto text-[10px] font-bold uppercase tracking-wider text-orange-600 opacity-0 group-hover:opacity-100 transition-opacity">
                        View →
                      </span>
                    )}
                  </>
                );
                return href ? (
                  <a
                    key={a}
                    href={href}
                    data-testid={testId}
                    className={`${commonClass} hover:border-orange-500 hover:bg-orange-50/40 hover:shadow-[0_4px_20px_-8px_rgba(234,88,12,0.35)] cursor-pointer`}
                  >
                    {contents}
                  </a>
                ) : (
                  <div
                    key={a}
                    data-testid={testId}
                    className={`${commonClass} hover:border-orange-500 hover:bg-orange-50/40`}
                  >
                    {contents}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* NEIGHBORHOODS + NAP block (local SEO signals) */}
        <div className="mt-14 lg:mt-20 pt-12 border-t border-stone-200 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <h3
              data-testid="neighborhoods-heading"
              className="font-display font-semibold text-2xl lg:text-3xl leading-tight tracking-tight text-stone-900"
            >
              Neighborhoods we serve across{" "}
              <span className="text-orange-600">St. Louis</span> &amp;{" "}
              <span className="text-orange-600">St. Charles County</span>
            </h3>
            <p className="mt-3 text-stone-600 text-[15px] leading-relaxed max-w-2xl">
              From historic city neighborhoods to modern subdivisions in the
              suburbs — we install flooring in every corner of the metro.
            </p>
            <ul
              data-testid="neighborhood-list"
              className="mt-6 flex flex-wrap gap-2"
            >
              {NEIGHBORHOODS.map((n) => (
                <li
                  key={n}
                  data-testid={`neighborhood-${n.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className="inline-flex items-center gap-1.5 bg-white border border-stone-200 rounded-full px-3 py-1.5 text-xs font-medium text-stone-700"
                >
                  <span className="w-1 h-1 bg-orange-500 rounded-full" />
                  {n}
                </li>
              ))}
            </ul>
          </div>

          {/* Contact / NAP + hours block */}
          <div className="lg:col-span-5">
            <div
              data-testid="areas-nap-block"
              className="bg-stone-900 text-stone-100 rounded-3xl p-7 lg:p-8"
              itemScope
              itemType="https://schema.org/LocalBusiness"
            >
              <meta itemProp="name" content="M&L Exterior and Interior Design" />
              <div className="text-xs uppercase tracking-[0.15em] text-orange-400 font-semibold mb-4">
                Visit · Call · Contact
              </div>
              <h4 className="font-display font-semibold text-xl text-stone-50 leading-tight mb-4">
                M&amp;L Exterior and Interior Design
              </h4>

              <div
                itemProp="address"
                itemScope
                itemType="https://schema.org/PostalAddress"
                className="text-sm text-stone-300 leading-relaxed"
                data-testid="areas-address"
              >
                <div>
                  <span itemProp="streetAddress">2013 Heather Drive</span>
                </div>
                <div>
                  <span itemProp="addressLocality">O&apos;Fallon</span>,{" "}
                  <span itemProp="addressRegion">MO</span>{" "}
                  <span itemProp="postalCode">63366</span>
                </div>
              </div>

              <a
                href="tel:+16368496681"
                itemProp="telephone"
                data-testid="areas-phone"
                className="mt-4 inline-flex items-center gap-2 text-orange-400 hover:text-orange-300 transition-colors font-semibold"
              >
                <Phone size={14} /> (636) 849-6681
              </a>

              <div className="mt-6 pt-6 border-t border-stone-800">
                <div className="text-xs uppercase tracking-wider text-orange-400 font-semibold mb-3">
                  Hours
                </div>
                <dl
                  data-testid="areas-hours"
                  className="text-sm space-y-1"
                  itemProp="openingHours"
                  content="Mo-Su 00:00-23:59"
                >
                  {[
                    "Monday",
                    "Tuesday",
                    "Wednesday",
                    "Thursday",
                    "Friday",
                    "Saturday",
                    "Sunday",
                  ].map((day) => (
                    <div
                      key={day}
                      data-testid={`areas-hours-${day.toLowerCase()}`}
                      className="flex justify-between text-stone-300"
                    >
                      <dt>{day}:</dt>
                      <dd className="text-stone-100 font-medium">
                        Open 24 hours
                      </dd>
                    </div>
                  ))}
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
